// Copyright ©️ 2026 eVolve MEP, LLC

import './catalog.css';

import React, { type ReactNode } from 'react';

import { type PartListPart, PART_TYPE_ASSEMBLY_ID } from '@evolvemep/api-ts';
import { Card, Flex, Overlay, Text, Box, Stack, type CardProps } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { ImageFromFile } from 'components/Mantine/FileUploads/ImageFromFile';
import { type GridMenuProps, GridMenu } from 'components/Mantine/GridMenu';
import { isNotNil } from 'helpers/isNotNil';

const cardHeight = 120;
const cardPadding = 12;
const imageSize = cardHeight - cardPadding * 4;

type Props<T extends string> = {
  ref?: React.Ref<HTMLDivElement>;
  part: PartListPart;
  disabled?: boolean;
  /** Component to render on the right side of the PartCard */
  children?: ReactNode;
  selected?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  cardProps?: CardProps;
} & (
  | {
      menu?: never;
      onMenuItemSelected?: (menuItemSelected: T) => void;
    }
  | {
      menu: GridMenuProps<T>['options'];
      onMenuItemSelected: (menuItemSelected: T) => void;
    }
);

export const PartCard = <T extends string = string>({
  ref,
  part,
  disabled,
  children,
  selected,
  fullWidth,
  onClick,
  cardProps,
  ...props
}: Props<T>) => (
  <Card
    ref={ref}
    className={`part-card ${onClick ? 'part-card-selectable' : ''} hover-title`}
    pr="md"
    pos="relative"
    radius="md"
    p={cardPadding}
    bg={selected ? 'var(--mantine-primary-color-1)' : undefined}
    w={fullWidth ? '100%' : undefined}
    mih={cardHeight}
    onClick={onClick}
    {...cardProps}
  >
    <Flex gap="sm" justify="space-between" flex={1}>
      <Flex gap="sm" flex={1}>
        <Stack justify="center">
          <ImageFromFile
            title={part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Assembly' : 'Part'}
            fileId={part.image?.fileId}
            size={imageSize}
            withContainer
            fallback={
              <EvolveIcon
                icon={part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Assembly' : 'Part'}
                size="lg"
                color="gray"
              />
            }
          >
            {isNotNil(part.image) && (
              <Box
                style={{
                  position: 'absolute',
                  left: 2,
                  bottom: 2,
                }}
              >
                <EvolveIcon className="shadow" icon={part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'Assembly' : 'Part'} />
              </Box>
            )}
          </ImageFromFile>
        </Stack>
        <Flex direction="column" justify="center">
          <Text title={part.name} fw={500} lineClamp={part.description ? 2 : 3}>
            {part.name}
          </Text>
          <Text fz="sm" c="gray.7" lineClamp={1} title={part.description ?? undefined}>
            {part.description}
          </Text>
          <Text
            fz="sm"
            c="gray.7"
            lineClamp={1}
            title={`Manufacturer: ${part.manufacturerName} | Catalog #: ${part.manufacturerCatalogNumber ?? 'N/A'}`}
          >
            {part.manufacturerName} {part.manufacturerCatalogNumber && <>– {part.manufacturerCatalogNumber}</>}
          </Text>
          <Text
            fz="sm"
            c="gray.7"
            lineClamp={1}
            title={`Unit of Measure: ${part.unitOfMeasureName} (${part.unitOfMeasureCode})`}
          >
            {part.unitOfMeasureName} ({part.unitOfMeasureCode})
          </Text>
        </Flex>
      </Flex>
      {children}
      {isNotNil(props.menu) && (
        <Box pos="absolute" top={cardPadding} right={cardPadding}>
          <GridMenu
            hoverClass="hover-title--on"
            options={props.menu}
            onMenuItemSelected={(item) => props.onMenuItemSelected?.(item)}
            size="sm"
          />
        </Box>
      )}
    </Flex>
    {disabled && <Overlay zIndex={2} color="#fff" />}
  </Card>
);
